import React, { createContext, SetStateAction, useContext, useState } from 'react';
import { usePost } from '../API';
import * as v0 from '@aidkitorg/types/lib/survey';
import { logAdminPanelError } from '../utils/adminPanel.utils';
import { 
  Dashboard, 
  DashboardComponentWithId, 
  StagedComponent, 
  SupportedDashboardComponent, 
  TranslatedComponentResult
} from '@aidkitorg/types/lib/admin-panel/dashboard';
import { v4 } from 'uuid';
import { Layout } from 'react-grid-layout';

export type DashboardWithComponents = Dashboard & {
  components: Array<DashboardComponentWithId>
}

export type HeroNumberWithFields = v0.HeroNumberWithFields & {description?: string}
export type BarChart = v0.BarChart & {description?: string}
export type PieChart = v0.PieChart & {description?: string}

type DashboardsContextType = {
  dashboards: Array<Dashboard>
  dashboardsById: DashboardsById,
  getDashboards: () => void
  isDashboardsLoading: boolean,  

  fetchDashboardById: (id: string) => void,
  getDashboardById: (id: string) => DashboardWithComponents | void,

  deleteDashboardById: (id: string) => void,

  // Dashboard Builder
  stagedComponents: Array<StagedComponent | DashboardComponentWithId>,
  setStagedComponents: React.Dispatch<SetStateAction<Array<StagedComponent | DashboardComponentWithId>>>,

  createDashboard: (title: string, description: string, components: Array<StagedComponent>, layout: Array<Layout>) => Promise<string | void>,
  updateDashboard: (id: string, title: string, description: string, components: Array<StagedComponent>, layout: Array<Layout>) => Promise<string | void>,
  getDashboardComponentTranslation: (component: SupportedDashboardComponent) => Promise<TranslatedComponentResult | void | null>

  logDashboardDownload: (id: string) => Promise<boolean>
}

type DashboardsById = Record<string, DashboardWithComponents>

type DashboardsProviderProps = {
  children: React.ReactNode
}

const DashboardsContext = createContext<DashboardsContextType>({
  dashboards: [],
  dashboardsById: {},
  getDashboards: async () => {},
  
  isDashboardsLoading: false,

  fetchDashboardById: async () => {},
  getDashboardById: () => {},

  deleteDashboardById: async () => {},

  stagedComponents: [],
  setStagedComponents: (_prev) => {},

  createDashboard: async (_title, _desc, _components, _layout) => {},
  updateDashboard: async (_id, _title, _desc, _components, _layout) => {},
  getDashboardComponentTranslation: async (_component: SupportedDashboardComponent) => {},

  logDashboardDownload: async (_id) => { return true },
})

export const useDashboards = () => {
  const context = useContext(DashboardsContext)
  if (!context) {
    throw new Error("useDashboards must be used within a DashboardsProvider")
  }

  return context;
}

export const DashboardsProvider: React.FC<DashboardsProviderProps> = ({ children }) => {
  // TODO: update this array type
  const [dashboards, setDashboards] = useState<Array<Dashboard>>([])
  const [isDashboardsLoading, setIsDashboardsLoading] = useState(false)

  const [stagedComponents, setStagedComponents] = useState<Array<StagedComponent>>([])

  const [dashboardsById, setDashboardsById] = useState<DashboardsById>({})

  const getAdminPanelDashboards = usePost("/api/admin_panel/dashboards")
  const fetchDashboardByIdPost = usePost("/api/admin_panel/dashboard")
  const translateDashboardComponent = usePost("/api/admin_panel/dashboards/component/translate")
  const createDashboardPost = usePost("/api/admin_panel/dashboards/create")
  const updateDashboardPost = usePost("/api/admin_panel/dashboards/update")
  const deleteDashboard = usePost("/api/admin_panel/dashboards/delete")
  const logDashboardDownloadPost = usePost("/api/admin_panel/dashboard/log_download")

  const getDashboards = async () => {
    setIsDashboardsLoading(true)
    const result = await getAdminPanelDashboards({})
    setIsDashboardsLoading(false)

    if (result.success && result.data) {
      setDashboards(result.data.dashboards)
    } else if (result.error) {
      logAdminPanelError(result.error, result.message)
    }
  }

  const fetchDashboardById = async (id: string) => {
    const result = await fetchDashboardByIdPost({dashboardId: id})

    if (result.success && result.data) {
      const dashboard = result.data
      // Transform legacy API response to match new DashboardComponentWithId structure
      const componentsWithIds: DashboardComponentWithId[] = dashboard.components
      
      const dashboardWithComponents: DashboardWithComponents = {
        id: dashboard.id,
        title: dashboard.title,
        description: dashboard.description,
        created_by: dashboard.created_by,
        created_at: dashboard.created_at,
        updated_at: dashboard.updated_at,
        view_only: dashboard.view_only,
        components: componentsWithIds
      }
      setDashboardsById((prev) => (
        {...prev,
          [dashboard.id]: dashboardWithComponents
        }
      ))
    } else if (result.error) {
      logAdminPanelError(result.error, result.message)
    }
  }

  const getDashboardById = (id: string) => {
    return dashboardsById[id]
  }

  const deleteDashboardById = async (id: string) => {
    const result = await deleteDashboard({dashboardId: id})

    if (result.success) {
      // Remove from both state variables
      console.log("deleted ", id)
      setDashboards(dashboards.filter((dashboard) => dashboard.id !== id))
      setDashboardsById(prev => {
        const newState = { ...prev }
        delete newState[id]
        return newState
      })
    }
  }

  const getDashboardComponentTranslation = async (component: SupportedDashboardComponent) => {
    const result = await translateDashboardComponent({component: {
      // Generates a temporary unique identifier for this translation request
      id: `temp-${v4()}`,
      config: component
    }})

    if (result.success) {
      return result.data?.result
    } else if (result.error) {
      logAdminPanelError(result.error, result.message)
    }
  }

  const createDashboard = async (title: string, description: string, components: Array<StagedComponent>, layout: Array<Layout>) => {
    const result = await createDashboardPost({
      title: title,
      description: description,
      components: components.map(sc => ({
        id: sc.id,
        config: sc.config,
        style: layout.find(l => l.i === sc.id) || {},
      }))
    })

    if (result.success && result.data) {
      return result.data.id;
    } else if (result.error) {
      logAdminPanelError(result.error, result.message)
    }
  }

  const updateDashboard = async (id: string, title: string, description: string, components: Array<StagedComponent>, layout: Array<Layout>) => {
    const result = await updateDashboardPost({
      dashboardId: id,
      title: title,
      description: description,
      components: components.map(sc => ({
        id: sc.id,
        config: sc.config,
        style: layout.find(l => l.i === sc.id) || {},
      }))
    })

    if (result.success && result.data) {
      return result.data.id;
    } else if (result.error) {
      logAdminPanelError(result.error, result.message)
    }
  }

  const logDashboardDownload = async (id: string) => {
    const result = await logDashboardDownloadPost({
      dashboardId: id,
    })

    if (result.error) {
      logAdminPanelError(result.error, result.message)
      return false
    }

    return true
  }

  const value = {
    dashboards,
    dashboardsById,
    getDashboards, 
    isDashboardsLoading,

    fetchDashboardById,
    getDashboardById,
    deleteDashboardById,

    stagedComponents,
    setStagedComponents,

    createDashboard,
    updateDashboard,
    getDashboardComponentTranslation,

    logDashboardDownload
  }
    
  return (
    <DashboardsContext.Provider value={value}>
      {children}
    </DashboardsContext.Provider>
  )
}